import { Box, Grid, HStack, Input, Link, Select, Text, VStack } from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { auth } from "../../../../../core/auth";
import {
  EntityProfileSummaryBlock as SummaryBlock,
  EntityProfileSummaryRow as SummaryRow,
} from "../../../../entityProfileModal/components/EntityProfileSummary";
import { entityProfileModalColors } from "../../../../entityProfileModal/entityProfileModal.theme";
import { queryKeys } from "../../../../../shared/query-keys";
import type { PatientId } from "../../../../../shared/schema/schema";
import useApi from "../../../../../shared/hooks/useApi";
import useEntityProfileToast from "../../../../entityProfileModal/hooks/useEntityProfileToast";
import type { PatientProfileBridgeActions } from "../../../patientProfileModal.actions";
import { formatPatientProfileValue } from "../../../patientProfileModal.utils";
import { PatientProfilePatient } from "../../../patientProfileModal.types";
import EntityModalButton from "../../../../entityProfileModal/components/EntityModalButton";
import EntityProfileEditModeButton from "../../../../entityProfileModal/components/EntityProfileEditModeButton";
import EntityProfileInlineEditableRow from "../../../../entityProfileModal/components/EntityProfileInlineEditableRow";

type Props = {
  actions: PatientProfileBridgeActions;
  patient?: PatientProfilePatient;
  patientId: PatientId;
};

type PatientContract = PatientProfilePatient["contracts"][number];
type ContractEditableField =
  | "endDate"
  | "isOnHold"
  | "isPrimary"
  | "medicaidNumber"
  | "medicareNumber"
  | "memberId"
  | "startDate";

type ContractDraft = Pick<PatientContract, ContractEditableField>;

function createContractDraft(contract: PatientContract): ContractDraft {
  return {
    endDate: contract.endDate,
    isOnHold: contract.isOnHold,
    isPrimary: contract.isPrimary,
    medicaidNumber: contract.medicaidNumber,
    medicareNumber: contract.medicareNumber,
    memberId: contract.memberId,
    startDate: contract.startDate,
  };
}

function toDateInputValue(value: PatientContract["startDate"] | PatientContract["endDate"]) {
  if (value === null || value === undefined) {
    return "";
  }

  return String(value).slice(0, 10);
}

function normalizeContractValue(
  field: ContractEditableField,
  value: ContractDraft[ContractEditableField]
) {
  if (
    (field === "endDate" || field === "medicaidNumber" || field === "medicareNumber") &&
    value === ""
  ) {
    return null;
  }

  return value;
}

function formatBoolean(value: boolean | null) {
  if (value === null) {
    return "";
  }

  return value ? "Yes" : "No";
}

function getPayerLabel(contract: PatientContract) {
  if (contract.hhaPayerName) {
    return contract.hhaPayerName;
  }

  if (contract.hhaPayer === null || contract.hhaPayer === undefined) {
    return "";
  }

  const payerName = contract.hhaPayer.hhaPayerName ?? "";
  const payerCode = contract.hhaPayer.hhaPayerCode ?? "";

  if (payerName === "" && payerCode === "") {
    return "";
  }

  return payerCode === "" ? payerName : `${payerName} (${payerCode})`;
}

function ContractFiles(props: { contract: PatientContract }) {
  if ((props.contract.fileDetails ?? []).length > 0) {
    return (
      <HStack flexWrap="wrap" justifyContent="flex-end" spacing={2}>
        {(props.contract.fileDetails ?? []).map((file) => (
          <Link
            key={file.id}
            isExternal
            className="btn btn-xs btn-primary btn-outline"
            href={file.url ?? undefined}
            pointerEvents={file.url === null ? "none" : undefined}
          >
            <i className="fa fa-external-link" /> {file.fileName ?? `File ${file.id}`}
          </Link>
        ))}
      </HStack>
    );
  }

  if ((props.contract.files ?? []).length === 0) {
    return null;
  }

  return (
    <HStack flexWrap="wrap" justifyContent="flex-end" spacing={2}>
      {(props.contract.files ?? []).map((file, index) => (
        <Link
          key={`${file}-${index}`}
          isExternal
          className="btn btn-xs btn-primary btn-outline"
          href={file}
        >
          <i className="fa fa-external-link" /> File {index + 1}
        </Link>
      ))}
    </HStack>
  );
}

function ContractCard(props: {
  actions: PatientProfileBridgeActions;
  contract: PatientContract;
  onDone: () => void;
  patient: PatientProfilePatient;
}) {
  const { api } = useApi();
  const toast = useEntityProfileToast();
  const { contract } = props;
  const canEditContracts = auth.isAllowedTo("edit_patient_page_contracts");
  const [isEditing, setIsEditing] = React.useState(false);
  const [activeField, setActiveField] = React.useState<ContractEditableField | null>(null);
  const [draft, setDraft] = React.useState<ContractDraft>(() => createContractDraft(contract));
  const updateContractMutation = useMutation({
    mutationFn: (params: { field: ContractEditableField; value: unknown }) =>
      api.anyRequest<void>(
        "PATCH",
        "/agencies/:agencyId/agency_members/:agencyMemberId/patients/:patientId/contracts/:contractId",
        {
          path: { contractId: contract.id, patientId: props.patient.id },
          body: { id: contract.id, [params.field]: params.value },
        }
      ),
    onSuccess: () => {
      toast({
        description: "Contract changed successfully.",
        status: "success",
      });
      setActiveField(null);
      props.onDone();
    },
    onError: () => {
      toast({
        description: "Could not edit contract.",
        status: "error",
      });
    },
  });

  React.useEffect(() => {
    setDraft(createContractDraft(contract));
  }, [contract]);

  const toggleEditing = () => {
    setIsEditing((currentValue) => {
      if (currentValue) {
        setActiveField(null);
        setDraft(createContractDraft(contract));
      }

      return !currentValue;
    });
  };

  const setDraftValue = (
    field: ContractEditableField,
    value: ContractDraft[ContractEditableField]
  ) => setDraft((currentDraft) => ({ ...currentDraft, [field]: value }));

  const commitField = (field: ContractEditableField) => {
    const value = normalizeContractValue(field, draft[field]);

    if (value === contract[field]) {
      setActiveField(null);
      return;
    }

    if (
      (field === "startDate" || field === "endDate") &&
      draft.startDate &&
      draft.endDate &&
      toDateInputValue(draft.endDate) < toDateInputValue(draft.startDate)
    ) {
      toast({
        description: "End date can't be before start date.",
        status: "error",
      });
      setDraft(createContractDraft(contract));
      return;
    }

    updateContractMutation.mutate({ field, value });
  };

  const cancelField = () => {
    setDraft(createContractDraft(contract));
    setActiveField(null);
  };

  const renderTextField = (field: "medicaidNumber" | "medicareNumber" | "memberId") => (
    <Input
      maxW="260px"
      size="sm"
      value={String(draft[field] ?? "")}
      onChange={(event) => setDraftValue(field, event.target.value)}
    />
  );

  const renderDateField = (field: "endDate" | "startDate") => (
    <Input
      maxW="170px"
      size="sm"
      type="date"
      value={toDateInputValue(draft[field])}
      onChange={(event) => setDraftValue(field, event.target.value)}
    />
  );

  const renderBooleanField = (field: "isOnHold" | "isPrimary") => (
    <Select
      maxW="120px"
      size="sm"
      value={draft[field] === null ? "" : String(draft[field])}
      onChange={(event) => {
        const value = event.target.value === "" ? null : event.target.value === "true";
        setDraftValue(field, value);
      }}
    >
      <option value="">empty</option>
      <option value="true">Yes</option>
      <option value="false">No</option>
    </Select>
  );

  const renderEditableRow = (
    field: ContractEditableField,
    label: string,
    value: React.ReactNode,
    editor: React.ReactNode
  ) => (
    <EntityProfileInlineEditableRow
      activeField={activeField}
      emptyPlaceholder={isEditing}
      field={field}
      isEditable={isEditing && !updateContractMutation.isPending}
      label={label}
      value={value}
      onActivate={setActiveField}
      onCancel={cancelField}
      onCommit={() => commitField(field)}
    >
      {editor}
    </EntityProfileInlineEditableRow>
  );

  return (
    <SummaryBlock
      actions={
        canEditContracts ? (
          <HStack alignItems="center" flexShrink={0} spacing={2}>
            <EntityModalButton
              kind="blue"
              size="sm"
              onClick={() =>
                props.actions.uploadPatientContractFile({
                  patient: props.patient,
                  data: contract,
                  onDone: props.onDone,
                })
              }
            >
              Upload contract file
            </EntityModalButton>
            <EntityProfileEditModeButton
              isDisabled={updateContractMutation.isPending}
              isEditing={isEditing}
              label="Edit"
              onClick={toggleEditing}
            />
          </HStack>
        ) : null
      }
      title={`Contract: ${contract.contractTypeName}`}
    >
      <Grid gap={{ base: 0, xl: 8 }} templateColumns={{ base: "1fr", xl: "repeat(2, 1fr)" }}>
        <VStack alignItems="stretch" spacing={0}>
          {renderEditableRow(
            "startDate",
            "Contract start date",
            formatPatientProfileValue(draft.startDate),
            renderDateField("startDate")
          )}
          {renderEditableRow(
            "endDate",
            "Contract end date",
            formatPatientProfileValue(draft.endDate),
            renderDateField("endDate")
          )}
          {renderEditableRow(
            "isPrimary",
            "Is primary",
            formatBoolean(draft.isPrimary),
            renderBooleanField("isPrimary")
          )}
          {renderEditableRow(
            "isOnHold",
            "Is on hold",
            formatBoolean(draft.isOnHold),
            renderBooleanField("isOnHold")
          )}
          <SummaryRow label="Contract Files" value={<ContractFiles contract={contract} />} />
        </VStack>

        <VStack alignItems="stretch" spacing={0}>
          {renderEditableRow(
            "memberId",
            "Member ID",
            formatPatientProfileValue(draft.memberId),
            renderTextField("memberId")
          )}
          {renderEditableRow(
            "medicaidNumber",
            "Medicaid Number",
            formatPatientProfileValue(draft.medicaidNumber),
            renderTextField("medicaidNumber")
          )}
          {renderEditableRow(
            "medicareNumber",
            "Medicare Number",
            formatPatientProfileValue(draft.medicareNumber),
            renderTextField("medicareNumber")
          )}
          <SummaryRow label="HHA payer" value={getPayerLabel(contract)} />
          <SummaryRow
            label="Treatments"
            value={contract.treatmentCount === 0 ? "" : contract.treatmentCount}
          />
        </VStack>
      </Grid>
    </SummaryBlock>
  );
}

export default function PatientProfileContractsSection(props: Props) {
  const queryClient = useQueryClient();
  const refreshPatient = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.patient.get(props.patientId) });
  const canEditContracts = auth.isAllowedTo("edit_patient_page_contracts");

  if (props.patient === undefined) {
    return <Text color={entityProfileModalColors.labelText}>Loading contracts...</Text>;
  }
  const patient = props.patient;

  return (
    <VStack alignItems="stretch" spacing={8}>
      {canEditContracts ? (
        <HStack justifyContent="flex-end">
          <EntityModalButton
            kind="blue"
            size="sm"
            onClick={() =>
              props.actions.openPatientContract({
                patient,
                onDone: refreshPatient,
              })
            }
          >
            Add Contract
          </EntityModalButton>
        </HStack>
      ) : null}
      {patient.contracts.length === 0 ? (
        <Box py={2}>
          <Text color={entityProfileModalColors.labelText} fontSize="14px" fontWeight={700}>
            No contracts
          </Text>
        </Box>
      ) : (
        patient.contracts.map((contract) => (
          <ContractCard
            key={contract.id}
            actions={props.actions}
            contract={contract}
            patient={patient}
            onDone={refreshPatient}
          />
        ))
      )}
    </VStack>
  );
}
